import { useState } from 'react';
import { AlertTriangle, History } from 'lucide-react';
import type { FiledPeriodFeeAdvisory, PreflightWarning } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { useCurrentPeriodReport } from '@/hooks/queries/useCurrentPeriodReport';
import { useFilingPreflight } from '@/hooks/queries/useFinanceReports';
import { useSortInboxQueue } from '@/hooks/queries/useSortInbox';
import { useBookProcessorFee } from '@/hooks/queries/useBookProcessorFee';
import { HardFailureList } from '../reports/HardFailureList';
import { PreflightUnready } from '@/components/finance/reports';

/**
 * Filing readiness on the close hub, BEFORE the File modal. Surfaces the
 * EXISTING `useFilingPreflight` hard-failure list (via the shared read-only
 * `HardFailureList`) so a treasurer sees what blocks the file without first
 * opening the modal.
 *
 * The loud `UnbookedProcessorFee` preflight warning (with the one-click "Book
 * fee" action) still lives on this surface — alert-don't-block, it never gates
 * the File button. The per-processor CALCULATED fee lines, by contrast, moved
 * OFF this banner surface into the ledger (Finding 10): `CloseLayout` reads
 * `preflight.processorFeeLines` and passes them to `TriageLedger`, which pins
 * them as dashed register rows at the top of the current-period group. They are
 * no longer rendered here.
 *
 * States:
 *   - no open period → render nothing (the hub already shows its own empty state)
 *   - period open, no report yet → nothing beyond fee advisories (the InboxStrip
 *     owns the "generate the report" readiness prompt — one readiness surface)
 *   - report exists, preflight loading → quiet loading line
 *   - report exists, preflight failed or offline → says readiness could not be checked, with Retry
 *   - report exists → hard failures (if any) + the fee nudge/lines. No "ready"
 *     line when nothing blocks: the readiness bar showing green IS that
 *     statement, and a second green voice re-creates the duplicate-status noise
 *     the treasurer redesign removed.
 */
export function CloseHubPreflight() {
  const current = useCurrentPeriodReport();
  const resolved = current.status === 'ready' ? current.data : null;
  const preflightRead = useFilingPreflight(resolved?.reportId ?? null);
  const queueRead = useSortInboxQueue(undefined);
  const queueData = queueRead.status === 'ready' ? queueRead.data : null;

  // The filed-period fee-discovery advisory is about PRIOR filed periods (a gift landed after
  // filing), so it surfaces independently of the current open period / report state — the
  // treasurer needs the nudge even between periods. Server-formatted, treasurer-first copy.
  const filedPeriodFeeAdvisories = queueData?.filedPeriodFeeAdvisories ?? [];
  const feeDiscovery = <FiledPeriodFeeDiscovery advisories={filedPeriodFeeAdvisories} />;

  const checkingLine = (
    <ReadinessShell>
      {feeDiscovery}
      <p className="text-sm text-muted-foreground">Checking filing readiness…</p>
    </ReadinessShell>
  );

  if (current.status === 'idle' || current.status === 'loading') return checkingLine;

  // No open period — the hub renders its own "no reporting period" empty state; filing
  // readiness has nothing to say EXCEPT a prior-period fee-discovery advisory, if any. An
  // unanswered health read is the same here: which report is owed is unknown (the masthead
  // says so), so no report's readiness can be named.
  if (!resolved?.period) {
    return filedPeriodFeeAdvisories.length > 0 ? (
      <ReadinessShell>{feeDiscovery}</ReadinessShell>
    ) : null;
  }

  if (!resolved.hasReport) {
    // The InboxStrip's readiness slot carries the "generate the report" prompt;
    // repeating it here would state the same fact twice on one screen.
    return filedPeriodFeeAdvisories.length > 0 ? (
      <ReadinessShell>{feeDiscovery}</ReadinessShell>
    ) : null;
  }

  // An unanswered preflight is never "nothing blocks": say so, so the treasurer doesn't read the
  // empty card as ready to file.
  if (preflightRead.status === 'failed' || preflightRead.status === 'offline') {
    return (
      <ReadinessShell>
        {feeDiscovery}
        <PreflightUnready read={preflightRead} reportId={resolved.reportId} />
      </ReadinessShell>
    );
  }

  // Report exists but the preflight hasn't resolved (loading) or returned null
  // (a 404 — unreachable here since `hasReport` means the report exists; we
  // treat it conservatively as still-checking rather than asserting "ready").
  if (preflightRead.status !== 'ready' || !preflightRead.data) return checkingLine;
  const preflight = preflightRead.data;

  const unbookedFeeWarnings = preflight.warnings.filter(
    (w) => w.warningType === 'UnbookedProcessorFee',
  );

  const hasHardFailures = preflight.hardFailures.length > 0;

  // Nothing blocking and nothing to nudge → render nothing. The green "ready"
  // line this surface used to show is retired: the readiness bar is the single
  // voice for "this period is fine".
  if (
    !hasHardFailures &&
    unbookedFeeWarnings.length === 0 &&
    filedPeriodFeeAdvisories.length === 0
  ) {
    return null;
  }

  return (
    <ReadinessShell>
      {feeDiscovery}

      {hasHardFailures && <HardFailureList hardFailures={preflight.hardFailures} />}

      <UnbookedFeeWarnings warnings={unbookedFeeWarnings} />
    </ReadinessShell>
  );
}

/**
 * The D3 nudge (spec §D3) as a loud, alert-don't-block preflight warning on the close hub:
 * the server-formatted `warningText` (processor + donation total + estimated fee) plus the
 * one-click "Book fee" action. "Dismiss" hides the row locally — the close hub is a read-only
 * readiness surface, so a real disposition (attest/dismiss) still happens in the File modal;
 * dismissing here just quiets the nudge for this view (it re-surfaces on the next preflight).
 */
function UnbookedFeeWarnings({
  warnings,
}: {
  warnings: PreflightWarning[];
}) {
  const { mutate: bookFee, isPending } = useBookProcessorFee();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = warnings.filter((w) => !dismissed.has(w.warningId));
  if (visible.length === 0) return null;

  return (
    <div
      data-testid="unbooked-processor-fee-warnings"
      className="space-y-2 rounded-md border border-amber-400 bg-amber-50 p-3 text-sm text-amber-900"
      role="alert"
    >
      {visible.map((warning) => (
        <div
          key={warning.warningId}
          data-testid="unbooked-processor-fee-warning"
          className="flex flex-wrap items-center justify-between gap-2"
        >
          <span className="flex items-start gap-1.5">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {warning.warningText}
          </span>
          <span className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              disabled={isPending}
              onClick={() =>
                bookFee({ processorId: warning.affectedRowIds[0] })
              }
            >
              Book fee
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() =>
                setDismissed((prev) => new Set(prev).add(warning.warningId))
              }
            >
              Dismiss
            </Button>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The filed-period fee-discovery advisory (ADR-007 alert-don't-block) — a gift landed in an
 * already-filed period, understating its processor fee. A NUDGE, never a filing gate: it tells
 * the treasurer to record the fee dated into the filed month (which flips that report to
 * NeedsAmendment via the general amendment check, ADR-019/020) or, for a coverage-gap period,
 * to file it directly in the portal. The `message` is server-formatted treasurer-first copy —
 * rendered verbatim, no enum translation needed at this boundary.
 */
function FiledPeriodFeeDiscovery({ advisories }: { advisories: FiledPeriodFeeAdvisory[] }) {
  if (advisories.length === 0) return null;

  return (
    <div
      data-testid="filed-period-fee-advisories"
      className="space-y-2 rounded-md border border-amber-300 bg-amber-50/70 p-3 text-sm text-amber-900"
      role="status"
    >
      {advisories.map((advisory) => (
        <p
          key={advisory.reportId}
          data-testid="filed-period-fee-advisory"
          className="flex items-start gap-1.5"
        >
          <History className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{advisory.message}</span>
        </p>
      ))}
    </div>
  );
}

function ReadinessShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-slot="close-preflight"
      data-testid="close-hub-preflight"
      className="space-y-3"
    >
      {children}
    </div>
  );
}
